// src/services/communityService.js
// Client for the Community backend (Expert Picks board + Chat board).
// Endpoints are mounted at /api/community/ (community/urls.py in the backend
// repo) — see /memories/repo/ notes. This file never fabricates data; every
// function is a thin wrapper around a real, confirmed endpoint/response shape.
import api from '../api';

// DRF validation errors arrive as {detail: "..."} or {field: ["msg", ...]}.
// Mirrors the flattening pattern already used in SubscriptionPage.js.
export function getApiErrorMessage(err, fallback = 'Something went wrong. Please try again.') {
  const data = err?.response?.data;
  if (!data) return fallback;
  if (typeof data.detail === 'string') return data.detail;
  const flattened = Object.values(data).flat().filter(v => typeof v === 'string');
  return flattened.length ? flattened.join(' ') : fallback;
}

export async function getCommunityBoards(signal) {
  const res = await api.get('community/boards/', { signal });
  return res.data;
}

// ─── Expert Picks ─────────────────────────────────────────────────────────
export async function getExpertPicks(page = 1, signal) {
  const res = await api.get('community/expert-picks/', { params: { page }, signal });
  return res.data;
}

// Admin-only: publish an existing CanonicalOdds opportunity as an Expert Pick.
export async function publishExpertPick({ canonicalOddsId, headline = '', commentary = '' }) {
  const res = await api.post('community/expert-picks/', {
    canonical_odds_id: canonicalOddsId,
    headline,
    commentary,
  });
  return res.data;
}

// Admin-only: headline/commentary are the only editable fields.
export async function updateExpertPick(id, { headline, commentary }) {
  const res = await api.patch(`community/expert-picks/${id}/`, { headline, commentary });
  return res.data;
}

// Admin-only: soft-remove (never deletes the underlying TrackedBet).
export async function removeExpertPick(id) {
  await api.delete(`community/expert-picks/${id}/`);
}

// ─── Expert Pick Comments ─────────────────────────────────────────────────
export async function getExpertPickComments(expertPickId, page = 1, signal) {
  const res = await api.get('community/expert-pick-comments/', {
    params: { expert_pick: expertPickId, page },
    signal,
  });
  return res.data;
}

export async function createExpertPickComment(expertPickId, content) {
  const res = await api.post('community/expert-pick-comments/', { expert_pick: expertPickId, content });
  return res.data;
}

export async function updateExpertPickComment(id, content) {
  const res = await api.patch(`community/expert-pick-comments/${id}/`, { content });
  return res.data;
}

export async function deleteExpertPickComment(id) {
  await api.delete(`community/expert-pick-comments/${id}/`);
}

// Admin-only moderation.
export async function hideExpertPickComment(id, reason = '') {
  const res = await api.post(`community/expert-pick-comments/${id}/hide/`, { reason });
  return res.data;
}

export async function unhideExpertPickComment(id) {
  const res = await api.post(`community/expert-pick-comments/${id}/unhide/`);
  return res.data;
}

// ─── Chat Messages ────────────────────────────────────────────────────────
export async function getChatMessages(page = 1, signal) {
  const res = await api.get('community/messages/', { params: { page }, signal });
  return res.data;
}

// `reference` is an optional object such as { reference_expert_pick: <id> }
// or { reference_tool_path, reference_tool_label } — at most one resource
// per message, enforced server-side.
export async function createChatMessage(content, reference) {
  const res = await api.post('community/messages/', { content, ...(reference || {}) });
  return res.data;
}

export async function updateChatMessage(id, content) {
  const res = await api.patch(`community/messages/${id}/`, { content });
  return res.data;
}

export async function deleteChatMessage(id) {
  await api.delete(`community/messages/${id}/`);
}

// Admin-only moderation.
export async function hideChatMessage(id, reason = '') {
  const res = await api.post(`community/messages/${id}/hide/`, { reason });
  return res.data;
}

export async function unhideChatMessage(id) {
  const res = await api.post(`community/messages/${id}/unhide/`);
  return res.data;
}
